import React, { useState, useEffect } from "react";
import axios from "axios";
import getAPI from "../../service/getAPI";

const FreeProductOffers = () => {
  const { master } = getAPI();

  const [offers, setOffers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const storeId = Number(localStorage.getItem("store_id") || "0");
  const isAdmin = user.user_type === "admin" || storeId === 0 || storeId === 1;

  const [formData, setFormData] = useState({
    buy_product_id: "",
    buy_qty: 1,
    free_products: [{ free_product_id: "", free_qty: 1 }],
    is_active: 1,
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const payload = isAdmin ? { branch_id: 0 } : { branch_id: storeId };
      const res = await axios.post(master.freeOfferGetAll, payload);
      if (res.data.flag === 1) {
        // Group by branch_id + buy_product_id + buy_qty to support multiple tiers per SKU
        const grouped = {};
        for (const row of res.data.data) {
          const groupKey = `${row.branch_id || 0}_${row.buy_product_id}_${row.buy_qty}`;
          if (!grouped[groupKey]) {
            grouped[groupKey] = {
              group_key: groupKey,
              buy_product_id: row.buy_product_id,
              buy_product_name: row.buy_product_name,
              buy_qty: row.buy_qty,
              branch_id: row.branch_id,
              is_active: row.is_active,
              free_products: [],
            };
          }
          grouped[groupKey].free_products.push({
            free_product_id: row.free_product_id,
            free_product_name: row.free_product_name,
            free_qty: row.free_qty,
          });
        }

        // Sort by product name, then buy_qty ascending
        const sorted = Object.values(grouped).sort((a, b) => {
          const nameA = a.buy_product_name || "";
          const nameB = b.buy_product_name || "";
          const cmp = nameA.localeCompare(nameB);
          if (cmp !== 0) return cmp;
          return Number(a.buy_qty) - Number(b.buy_qty);
        });

        setOffers(sorted);
      }
    } catch (err) {
      console.error("Error fetching offers:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.post(master.productSelectAll, { pageIndex: 1, pageSize: 500 });
      if (res.data.flag === 1) setProducts(res.data.data.result || res.data.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchProducts();
  }, []);

  const openAddModal = () => {
    setEditingOffer(null);
    setFormData({
      buy_product_id: "",
      buy_qty: 1,
      free_products: [{ free_product_id: "", free_qty: 1 }],
      is_active: 1,
    });
    setShowModal(true);
  };

  const openEditModal = (offer) => {
    setEditingOffer(offer);
    setFormData({
      buy_product_id: offer.buy_product_id,
      buy_qty: offer.buy_qty,
      free_products: offer.free_products.map((fp) => ({
        free_product_id: fp.free_product_id,
        free_qty: fp.free_qty,
      })),
      is_active: offer.is_active,
    });
    setShowModal(true);
  };

  const handleFreeProductChange = (index, field, value) => {
    const updated = [...formData.free_products];
    updated[index][field] = value;
    setFormData({ ...formData, free_products: updated });
  };

  const addFreeProductRow = () => {
    setFormData({
      ...formData,
      free_products: [...formData.free_products, { free_product_id: "", free_qty: 1 }],
    });
  };

  const removeFreeProductRow = (index) => {
    const updated = formData.free_products.filter((_, i) => i !== index);
    setFormData({ ...formData, free_products: updated });
  };

  const handleSubmit = async () => {
    if (!formData.buy_product_id || formData.free_products.some((fp) => !fp.free_product_id)) {
      alert("Please fill in all required fields.");
      return;
    }

    const buyProdId = Number(formData.buy_product_id);
    const buyQty = Number(formData.buy_qty);

    // Duplicate check
    const isDuplicate = offers.some(o => {
      if (
        editingOffer &&
        Number(o.buy_product_id) === Number(editingOffer.buy_product_id) &&
        Number(o.buy_qty) === Number(editingOffer.buy_qty)
      ) {
        return false;
      }
      return Number(o.buy_product_id) === buyProdId && Number(o.buy_qty) === buyQty;
    });

    if (isDuplicate) {
      alert(`An offer bundle for this product with Buy Quantity ${buyQty} already exists. Please edit that bundle or choose a different quantity.`);
      return;
    }

    try {
      const payload = {
        branch_id: isAdmin ? 0 : storeId,
        buy_product_id: buyProdId,
        buy_qty: buyQty,
        free_products: formData.free_products.map((fp) => ({
          free_product_id: Number(fp.free_product_id),
          free_qty: Number(fp.free_qty),
        })),
        is_active: formData.is_active,
      };

      if (editingOffer) {
        payload.original_buy_product_id = editingOffer.buy_product_id;
        payload.original_buy_qty = editingOffer.buy_qty;
      }

      const url = editingOffer ? master.freeOfferUpdate : master.freeOfferAdd;
      await axios.post(url, payload);
      setShowModal(false);
      fetchAll();
    } catch (err) {
      console.error("Error saving offer:", err);
      alert(err.response?.data?.message || "Error saving offer");
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Delete free gift offer bundle for ${offer.buy_product_name || 'this product'} (Buy Qty: ${offer.buy_qty})?`)) return;
    try {
      await axios.post(master.freeOfferDelete, {
        branch_id: isAdmin ? 0 : storeId,
        buy_product_id: offer.buy_product_id,
        buy_qty: offer.buy_qty,
      });
      fetchAll();
    } catch (err) {
      console.error("Error deleting offer:", err);
    }
  };

  const productOptions = products.map((p) => (
    <option key={p.product_id} value={p.product_id}>
      {p.product_name} ({p.product_weight})
    </option>
  ));

  return (
    <main id="main" className="main">
      <div className="pagetitle d-flex justify-content-between align-items-center">
        <div>
          <h1>Free Product Offers</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">Home</li>
              <li className="breadcrumb-item active">Free Product Offers</li>
            </ol>
          </nav>
        </div>
        <button className="btn btn-success btn-sm" onClick={openAddModal}>
          <i className="bi bi-plus-lg me-1"></i> Add Offer
        </button>
      </div>

      <section className="section">
        <div className="card">
          <div className="card-body pt-3">
            {loading ? (
              <div className="text-center py-4"><div className="spinner-border text-success"></div></div>
            ) : offers.length === 0 ? (
              <div className="text-center py-4 text-muted">No free product offers configured yet.</div>
            ) : (
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Buy Product</th>
                    <th>Buy Qty</th>
                    <th>Free Gifts</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((offer, i) => (
                    <tr key={offer.group_key}>
                      <td>{i + 1}</td>
                      <td><strong>{offer.buy_product_name}</strong></td>
                      <td>
                        <span className="badge bg-primary fs-6">{offer.buy_qty}</span>
                      </td>
                      <td>
                        <ul className="mb-0 ps-3">
                          {offer.free_products.map((fp, j) => (
                            <li key={j}>{fp.free_product_name} &times; {fp.free_qty}</li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        <span className={`badge ${offer.is_active ? "bg-success" : "bg-secondary"}`}>
                          {offer.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => openEditModal(offer)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(offer)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingOffer ? "Edit Offer Bundle" : "Add Offer Bundle"}</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-8">
                    <label className="form-label">Buy Product *</label>
                    <select
                      className="form-select"
                      value={formData.buy_product_id}
                      onChange={(e) => setFormData({ ...formData, buy_product_id: e.target.value })}
                      disabled={!!editingOffer}
                    >
                      <option value="">-- Select Product --</option>
                      {productOptions}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Minimum Buy Qty *</label>
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      value={formData.buy_qty}
                      onChange={(e) => setFormData({ ...formData, buy_qty: e.target.value })}
                    />
                  </div>
                </div>

                <hr />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Free Gifts</strong>
                  <button className="btn btn-sm btn-outline-success" onClick={addFreeProductRow}>
                    <i className="bi bi-plus-lg me-1"></i> Add Another Free Gift
                  </button>
                </div>

                {formData.free_products.map((fp, idx) => (
                  <div className="row mb-2 align-items-end" key={idx}>
                    <div className="col-md-8">
                      <label className="form-label">Free Product {idx + 1} *</label>
                      <select
                        className="form-select"
                        value={fp.free_product_id}
                        onChange={(e) => handleFreeProductChange(idx, "free_product_id", e.target.value)}
                      >
                        <option value="">-- Select Product --</option>
                        {productOptions}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Free Qty</label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        value={fp.free_qty}
                        onChange={(e) => handleFreeProductChange(idx, "free_qty", e.target.value)}
                      />
                    </div>
                    <div className="col-md-1">
                      {formData.free_products.length > 1 && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => removeFreeProductRow(idx)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                <hr />
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActiveSwitch"
                    checked={formData.is_active === 1}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                  <label className="form-check-label" htmlFor="isActiveSwitch">Active</label>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-success" onClick={handleSubmit}>Save Offer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default FreeProductOffers;
